import React from 'react';
import { Undo2 } from 'lucide-react';
import { Button, Card, Pill } from '../../../components/ui';
import { RecordSummary, SignalList, VerdictMark } from './ui';
import type { DuplicateCandidate } from '../../../types/duplicates';

interface CandidateCardProps {
  candidate: DuplicateCandidate;
  busy?: boolean;
  onDismiss: (candidate: DuplicateCandidate) => void;
  onMerge: (candidate: DuplicateCandidate) => void;
}

/**
 * One pair, as a reviewer has to see it: both records side by side, what
 * hangs off each of them, and the evidence that put them together. Two ways
 * out — they are different people, or they are one.
 */
export const CandidateCard: React.FC<CandidateCardProps> = ({ candidate, busy = false, onDismiss, onMerge }) => (
  <Card>
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-3">
      <div className="flex items-center gap-2">
        <VerdictMark verdict={candidate.verdict} score={candidate.score} />
        {candidate.dismissed && <Pill tone="outline">Not duplicates</Pill>}
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={busy || candidate.dismissed}
          onClick={() => onDismiss(candidate)}
        >
          <Undo2 className="h-4 w-4" strokeWidth={1.75} />
          Not duplicates
        </Button>
        <Button size="sm" variant="dark" disabled={busy} onClick={() => onMerge(candidate)}>
          Merge
        </Button>
      </div>
    </div>

    <div className="grid grid-cols-1 border-t border-neutral-200/70 sm:grid-cols-2">
      <RecordSummary side={candidate.left} entityType={candidate.entityType} className="p-4" />
      <RecordSummary
        side={candidate.right}
        entityType={candidate.entityType}
        className="border-t border-neutral-200/70 p-4 sm:border-l sm:border-t-0"
      />
    </div>

    {candidate.signals.length > 0 && (
      <div className="border-t border-neutral-200/70 px-4 py-3">
        <SignalList signals={candidate.signals} />
      </div>
    )}
  </Card>
);
